import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getPersonFields, updatePersonFields } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { formatDisplayDate as formatDate } from '@/lib/dates';

const FieldsContext = createContext(null);
const SectionContext = createContext('');
const types = { text: 'Text', multiline: 'Long text', number: 'Number', date: 'Date', select: 'Dropdown', checkbox: 'Yes / No' };
const selectClass = 'h-10 rounded-md border border-[#062B1A]/20 bg-white px-3 text-sm';

export function PersonFieldsProvider({ children, values = {}, editing, onChange }) {
  const [schema, setSchema] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    if (!getPersonFields) return undefined;
    getPersonFields().then(({ data }) => { if (active) setSchema(data); })
      .catch(() => { if (active) setError('Custom fields could not load. Reload to try again.'); });
    return () => { active = false; };
  }, []);
  return <FieldsContext.Provider value={{ schema, setSchema, values, editing, onChange }}>
    {error && <p role="alert" className="rounded-lg border p-3 text-sm">{error}</p>}
    {children}
  </FieldsContext.Provider>;
}

export function usePersonField(label) {
  const context = useContext(FieldsContext);
  const section = useContext(SectionContext);
  const fields = context?.schema?.fields || [];
  const index = fields.findIndex((field) => field.section === section && field.type === 'builtin'
    && field.source_label.toLowerCase() === String(label).toLowerCase());
  return index < 0 ? { label, visible: true } : { ...fields[index], order: index };
}

export function ProfileFieldSlot({ label, children, className }) {
  const field = usePersonField(label);
  if (!field.visible) return null;
  return <div className={className} style={{ order: field.order }}>{children}</div>;
}

export function ProfileFieldLabel({ label, ...props }) {
  const field = usePersonField(label);
  return <Label {...props}>{field.label}</Label>;
}

function CustomField({ field, context }) {
  const value = context.values?.[field.id];
  const change = (next) => context.onChange?.(field.id, next);
  const id = `person-field-${field.id}`;
  let control;
  if (!context.editing) {
    const display = value === null || value === undefined || value === '' ? '—'
      : field.type === 'checkbox' ? (value ? 'Yes' : 'No')
        : field.type === 'date' ? formatDate(value) : String(value);
    control = <p className="whitespace-pre-wrap break-words text-sm font-medium text-[#062B1A]">{display}</p>;
  } else if (field.type === 'multiline') {
    control = <Textarea id={id} value={value ?? ''} maxLength={10000} onChange={(event) => change(event.target.value)} />;
  } else if (field.type === 'select' || field.type === 'checkbox') {
    const options = field.type === 'checkbox' ? ['true', 'false'] : field.options || [];
    const current = value === null || value === undefined ? '' : String(value);
    control = <select id={id} className={`${selectClass} w-full`} value={current}
      onChange={(event) => change(event.target.value === '' ? null : field.type === 'checkbox' ? event.target.value === 'true' : event.target.value)}>
      <option value="">Not set</option>
      {current && !options.includes(current) && <option value={current}>{current} (saved value)</option>}
      {options.map((option) => <option key={option} value={option}>{field.type === 'checkbox' ? (option === 'true' ? 'Yes' : 'No') : option}</option>)}
    </select>;
  } else {
    control = <Input id={id} type={field.type === 'text' ? 'text' : field.type} step={field.type === 'number' ? 'any' : undefined}
      maxLength={1000} value={value ?? ''} onChange={(event) => change(event.target.value === '' ? null : field.type === 'number' ? Number(event.target.value) : event.target.value)} />;
  }
  return <div className="min-w-0 space-y-1.5"><Label htmlFor={context.editing ? id : undefined}>{field.label}</Label>{control}</div>;
}

export function PersonFieldSection({ id, children }) {
  const context = useContext(FieldsContext);
  const custom = (context?.schema?.fields || []).filter((field) => field.section === id && field.type !== 'builtin' && field.visible);
  return <SectionContext.Provider value={id}>
    {children}
    {custom.length > 0 && <div className="mt-2 grid grid-cols-1 gap-4 rounded-xl border bg-white p-6 sm:grid-cols-2">
      {custom.map((field) => <CustomField key={field.id} field={field} context={context} />)}
    </div>}
  </SectionContext.Provider>;
}

export function PersonFieldsStandalone({ ids, controller }) {
  const context = useContext(FieldsContext);
  if (!context?.schema) return null;
  return ids.filter((id) => controller.sectionById[id]?.visible !== false
    && context.schema.fields.some((field) => field.section === id && field.type !== 'builtin' && field.visible))
    .map((id) => <div key={id} className="lg:col-span-12" style={{ order: controller.sectionById[id]?.order }}>
      <h3 className="text-sm font-semibold text-[#062B1A]">{controller.sectionById[id]?.title || context.schema.sections[id]}</h3>
      <PersonFieldSection id={id} />
    </div>);
}

export function PersonFieldsToolbar({ disabled = false }) {
  const context = useContext(FieldsContext);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [section, setSection] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('text');
  const [choices, setChoices] = useState('');
  if (!context?.schema) return null;
  const begin = async () => {
    try {
      const { data } = await getPersonFields();
      context.setSchema(data);
      setDraft(JSON.parse(JSON.stringify(data)));
      setError(''); setOpen(true);
    } catch { toast.error('Could not load field settings. Please try again.'); }
  };
  const patch = (id, update) => setDraft((current) => ({ ...current, fields: current.fields.map((field) => field.id === id ? { ...field, ...update } : field) }));
  const move = (id, direction) => setDraft((current) => {
    const list = [...current.fields];
    const siblings = list.filter((field) => field.section === section && (field.type === 'builtin') === (list.find((item) => item.id === id).type === 'builtin'));
    const siblingIndex = siblings.findIndex((field) => field.id === id);
    const target = siblings[siblingIndex + direction];
    if (!target) return current;
    const from = list.findIndex((field) => field.id === id);
    const to = list.findIndex((field) => field.id === target.id);
    const [item] = list.splice(from, 1); list.splice(to, 0, item);
    return { ...current, fields: list };
  });
  const add = () => {
    const options = [...new Set(choices.split('\n').map((choice) => choice.trim()).filter(Boolean))];
    if (!label.trim() || (kind === 'select' && !options.length)) { setError('Enter a field label and at least one choice for a dropdown.'); return; }
    setDraft((current) => ({ ...current, fields: [...current.fields, {
      id: `custom_${crypto.randomUUID()}`, section, label: label.trim(), type: kind, visible: true,
      ...(kind === 'select' ? { options } : {}),
    }] }));
    setLabel(''); setChoices(''); setError('');
  };
  const save = async () => {
    setSaving(true); setError('');
    try {
      const { data } = await updatePersonFields({ revision: draft.revision, fields: draft.fields });
      context.setSchema(data); setOpen(false); toast.success('Fields saved for all student and admissions profiles');
    } catch (failure) {
      const detail = failure?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save field settings. Check the labels and choices, then try again.');
    } finally { setSaving(false); }
  };
  return <>
    <Button type="button" variant="outline" disabled={disabled} onClick={begin}>Manage fields</Button>
    <Dialog open={open} onOpenChange={(value) => { if (!saving) setOpen(value); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>Manage profile fields</DialogTitle>
          <DialogDescription>These settings apply to everyone in Students and Admissions. Hiding a field preserves all saved values; use Show to restore it. Each person keeps their own answers.</DialogDescription>
        </DialogHeader>
        <Label htmlFor="field-section">Section</Label>
        <select id="field-section" className={selectClass} value={section} onChange={(event) => setSection(event.target.value)} disabled={saving}>
          {Object.entries(context.schema.sections).map(([id, title]) => <option key={id} value={id}>{title}</option>)}
        </select>
        <p className="text-xs text-[#062B1A]/60">Existing fields keep their validation and calculations. Custom fields appear below the section’s existing content. Use Customize layout to rename, arrange or hide whole sections.</p>
        <div className="space-y-3">
          {draft?.fields.filter((field) => field.section === section).map((field) => <div key={field.id} className={`rounded-lg border p-3 ${!field.visible ? 'bg-slate-50' : ''}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Input aria-label={`Label for ${field.source_label || field.label}`} value={field.label} maxLength={80} disabled={saving}
                onChange={(event) => patch(field.id, { label: event.target.value })} className="min-w-40 flex-1" />
              <span className="text-xs text-[#062B1A]/55">{field.type === 'builtin' ? 'Existing field' : types[field.type]}</span>
              <Button type="button" variant="outline" disabled={saving} onClick={() => patch(field.id, { visible: !field.visible })}>{field.visible ? 'Hide' : 'Show'}</Button>
              <Button type="button" variant="ghost" disabled={saving} aria-label={`Move ${field.label} up`} onClick={() => move(field.id, -1)}>↑</Button>
              <Button type="button" variant="ghost" disabled={saving} aria-label={`Move ${field.label} down`} onClick={() => move(field.id, 1)}>↓</Button>
            </div>
            {field.type === 'select' && <Textarea className="mt-2" aria-label={`Choices for ${field.label}`} disabled={saving}
              value={field.options.join('\n')} onChange={(event) => patch(field.id, { options: event.target.value.split('\n') })} />}
          </div>)}
        </div>
        <fieldset className="space-y-3 rounded-lg border p-4" disabled={saving}>
          <legend className="px-1 text-sm font-semibold">Add a field</legend>
          <Input aria-label="New field label" placeholder="Field label, e.g. Emergency contact" maxLength={80} value={label} onChange={(event) => setLabel(event.target.value)} />
          <select className={selectClass} aria-label="New field type" value={kind} onChange={(event) => setKind(event.target.value)}>
            {Object.entries(types).map(([type, title]) => <option key={type} value={type}>{title}</option>)}
          </select>
          {kind === 'select' && <Textarea aria-label="New field choices" placeholder="One choice per line" value={choices} onChange={(event) => setChoices(event.target.value)} />}
          <Button type="button" variant="outline" onClick={add}>Add field</Button>
        </fieldset>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" disabled={saving} onClick={() => setOpen(false)}>Cancel</Button>
          <Button type="button" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save fields for everyone'}</Button></div>
      </DialogContent>
    </Dialog>
  </>;
}
